import {BaseBadge, BaseButton, BaseCard, BaseInput, BaseModal, BaseTable} from "@/components";
import type {Column} from "@/components/BaseTable/BaseTable";
import {
  useCreateProvider,
  useDeleteProvider,
  useProviders,
  useToggleProviderActive,
  useUpdateProvider,
} from "@/hooks/useProviders";
import type {Provider} from "@/services/providers.service";
import {Pause, Pencil, Play, Trash2} from "lucide-react";
import {useState} from "react";

export function Providers() {
  const {data: providers, isLoading} = useProviders();
  const toggleActive = useToggleProviderActive();
  const deleteProvider = useDeleteProvider();
  const [showCreate, setShowCreate] = useState(false);
  const [editing, setEditing] = useState<Provider | null>(null);

  const items = providers ?? [];

  const columns: Column<Provider>[] = [
    {
      key: "code",
      header: "Código",
      render: row => <span className="text-sm font-mono text-(--color-text-primary)">{row.code}</span>,
    },
    {
      key: "name",
      header: "Nombre",
      render: row => <span className="text-sm font-medium text-(--color-text-primary)">{row.name}</span>,
    },
    {
      key: "description",
      header: "Descripción",
      render: row => (
        <span className="text-xs text-(--color-text-tertiary) max-w-48 truncate block">{row.description || "—"}</span>
      ),
    },
    {
      key: "is_active",
      header: "Estado",
      render: row => (
        <BaseBadge
          variant={row.is_active ? "success" : "neutral"}
          label={row.is_active ? "Activo" : "Inactivo"}
          dot
        />
      ),
    },
    {
      key: "actions",
      header: "Acciones",
      align: "right",
      render: row => (
        <div className="flex items-center gap-1 justify-end">
          <BaseButton
            variant="ghost"
            size="sm"
            onClick={() => setEditing(row)}
            title="Editar"
          >
            <Pencil size={14} />
          </BaseButton>
          <BaseButton
            variant="ghost"
            size="sm"
            loading={toggleActive.isPending}
            onClick={() => toggleActive.mutate({id: row.id, isActive: !row.is_active})}
            title={row.is_active ? "Desactivar" : "Activar"}
          >
            {row.is_active ? <Pause size={14} /> : <Play size={14} />}
          </BaseButton>
          <BaseButton
            variant="ghost"
            size="sm"
            onClick={() => {
              if (confirm(`¿Eliminar proveedor ${row.name}?`)) deleteProvider.mutate(row.id);
            }}
            title="Eliminar"
          >
            <Trash2 size={14} />
          </BaseButton>
        </div>
      ),
    },
  ];

  return (
    <div className="flex flex-col gap-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-(--color-text-primary)">Proveedores</h1>
          <p className="text-sm text-(--color-text-secondary) mt-1">
            {items.length} proveedores — Catálogo maestro de proveedores de pago
          </p>
        </div>
        <BaseButton
          variant="primary"
          size="md"
          onClick={() => setShowCreate(true)}
        >
          + Nuevo proveedor
        </BaseButton>
      </div>

      <BaseCard padding="none">
        <BaseTable<Provider>
          data={items}
          columns={columns}
          loading={isLoading}
          emptyMessage="No hay proveedores configurados."
          onRowClick={row => setEditing(row)}
        />
      </BaseCard>

      {showCreate && <CreateProviderModal onClose={() => setShowCreate(false)} />}
      {editing && (
        <EditProviderModal
          provider={editing}
          onClose={() => setEditing(null)}
        />
      )}
    </div>
  );
}

function CreateProviderModal({onClose}: {onClose: () => void}) {
  const createProvider = useCreateProvider();
  const [form, setForm] = useState({
    code: "",
    name: "",
    logo: "",
    website: "",
    description: "",
  });
  const [error, setError] = useState("");

  const handleSubmit = async (e: React.SyntheticEvent) => {
    e.preventDefault();
    setError("");
    if (!form.code.trim() || !form.name.trim()) {
      setError("Código y nombre son requeridos");
      return;
    }
    try {
      await createProvider.mutateAsync({
        code: form.code.trim(),
        name: form.name.trim(),
        logo: form.logo.trim(),
        website: form.website.trim(),
        description: form.description.trim(),
        is_active: false,
      });
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Error al crear el proveedor");
    }
  };

  const set = (key: string, value: string) => setForm(prev => ({...prev, [key]: value}));

  return (
    <BaseModal
      open
      onClose={onClose}
      title="Nuevo Proveedor"
      size="md"
      footer={
        <>
          <BaseButton
            variant="ghost"
            size="md"
            onClick={onClose}
          >
            Cancelar
          </BaseButton>
          <BaseButton
            variant="primary"
            size="md"
            loading={createProvider.isPending}
            onClick={handleSubmit}
          >
            Crear
          </BaseButton>
        </>
      }
    >
      <form
        onSubmit={handleSubmit}
        className="flex flex-col gap-4"
      >
        <div className="grid grid-cols-2 gap-3">
          <BaseInput
            label="Código"
            placeholder="ej. zpay24_pk"
            value={form.code}
            onChange={e => set("code", e.target.value)}
          />
          <BaseInput
            label="Nombre"
            placeholder="ej. ZPay24 (PK)"
            value={form.name}
            onChange={e => set("name", e.target.value)}
          />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <BaseInput
            label="Logo URL"
            placeholder="https://..."
            value={form.logo}
            onChange={e => set("logo", e.target.value)}
          />
          <BaseInput
            label="Website"
            placeholder="https://..."
            value={form.website}
            onChange={e => set("website", e.target.value)}
          />
        </div>
        <BaseInput
          label="Descripción"
          placeholder="Descripción interna"
          value={form.description}
          onChange={e => set("description", e.target.value)}
        />
        {error && <p className="text-sm text-(--color-danger)">{error}</p>}
      </form>
    </BaseModal>
  );
}

function EditProviderModal({provider, onClose}: {provider: Provider; onClose: () => void}) {
  const updateProvider = useUpdateProvider();
  const [form, setForm] = useState({
    name: provider.name,
    logo: provider.logo,
    website: provider.website,
    description: provider.description,
  });
  const [error, setError] = useState("");

  const handleSubmit = async (e: React.SyntheticEvent) => {
    e.preventDefault();
    setError("");
    if (!form.name.trim()) {
      setError("El nombre es requerido");
      return;
    }
    try {
      await updateProvider.mutateAsync({
        id: provider.id,
        body: {
          name: form.name.trim(),
          logo: form.logo.trim(),
          website: form.website.trim(),
          description: form.description.trim(),
        },
      });
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Error al actualizar");
    }
  };

  const set = (key: string, value: string) => setForm(prev => ({...prev, [key]: value}));

  return (
    <BaseModal
      open
      onClose={onClose}
      title={`Editar — ${provider.name}`}
      size="md"
      footer={
        <>
          <BaseButton
            variant="ghost"
            size="md"
            onClick={onClose}
          >
            Cancelar
          </BaseButton>
          <BaseButton
            variant="primary"
            size="md"
            loading={updateProvider.isPending}
            onClick={handleSubmit}
          >
            Guardar
          </BaseButton>
        </>
      }
    >
      <form
        onSubmit={handleSubmit}
        className="flex flex-col gap-4"
      >
        <div className="p-3 rounded-md bg-(--color-surface-2) text-xs text-(--color-text-secondary)">
          <span className="font-medium">Código:</span> {provider.code}
        </div>
        <BaseInput
          label="Nombre"
          value={form.name}
          onChange={e => set("name", e.target.value)}
        />
        <div className="grid grid-cols-2 gap-3">
          <BaseInput
            label="Logo URL"
            value={form.logo}
            onChange={e => set("logo", e.target.value)}
          />
          <BaseInput
            label="Website"
            value={form.website}
            onChange={e => set("website", e.target.value)}
          />
        </div>
        <BaseInput
          label="Descripción"
          value={form.description}
          onChange={e => set("description", e.target.value)}
        />
        {error && <p className="text-sm text-(--color-danger)">{error}</p>}
      </form>
    </BaseModal>
  );
}
